import { useEffect, useState, type MouseEvent } from "react";
import type { DetailOption } from "@/mocks/site";

type DetailModalProps = {
  open: boolean;
  onClose: () => void;
  unitLabel: string;
  title: string;
  image: string;
  price?: string;
  strikePrice?: string;
  metaNote?: string;
  description: string;
  optionsTitle?: string;
  options?: DetailOption[];
  includesTitle?: string;
  includes?: string[];
  conditionsTitle?: string;
  conditions?: string[];
  ctaLabel: string;
  ctaIcon?: string;
  buildCtaHref: (option?: DetailOption) => string;
};

export default function DetailModal({
  open,
  onClose,
  unitLabel,
  title,
  image,
  price,
  strikePrice,
  metaNote,
  description,
  optionsTitle,
  options,
  includesTitle,
  includes,
  conditionsTitle,
  conditions,
  ctaLabel,
  ctaIcon = "ri-whatsapp-line",
  buildCtaHref,
}: DetailModalProps) {
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (open) setSelected(0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const activeOption = options && options.length > 0 ? options[selected] : undefined;
  const displayedPrice = activeOption?.price ?? price;

  const handleBackdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-primary-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={handleBackdrop}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="card-soft-lg relative max-h-full w-full max-w-4xl overflow-y-auto rounded-t-[24px] border border-secondary-200 bg-background-50 sm:max-h-[90vh] sm:rounded-[24px]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar detalle"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-secondary-200 bg-background-50/90 text-foreground-900 transition-colors hover:bg-primary-500 hover:text-background-50"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative h-[280px] w-full overflow-hidden rounded-t-[24px] bg-background-100 md:h-full md:min-h-[560px] md:rounded-l-[24px] md:rounded-tr-none">
            <img
              src={image}
              alt={title}
              title={`${title} ALÉA Aesthetic House`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="flex flex-col p-6 md:p-9">
            <span className="font-label text-[11px] font-medium uppercase tracking-[0.22em] text-accent-700">
              {unitLabel}
            </span>
            <h3 className="mt-3 font-heading text-3xl font-bold leading-[1.08] text-foreground-950 md:text-[32px]">
              {title}
            </h3>

            {displayedPrice ? (
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-heading text-2xl font-bold text-foreground-950">
                  {displayedPrice}
                </span>
                {strikePrice ? (
                  <span className="font-body text-base text-foreground-400 line-through">
                    {strikePrice}
                  </span>
                ) : null}
              </div>
            ) : null}

            {metaNote ? (
              <span className="mt-3 inline-flex w-fit items-center gap-2 rounded-full bg-accent-100 px-3 py-1 font-label text-[10px] uppercase tracking-[0.14em] text-accent-900">
                <i className="ri-calendar-line text-xs" />
                {metaNote}
              </span>
            ) : null}

            <p className="mt-4 text-[15px] leading-relaxed text-foreground-600">{description}</p>

            {options && options.length > 0 ? (
              <div className="mt-6">
                <span className="font-label text-[11px] uppercase tracking-[0.18em] text-foreground-500">
                  {optionsTitle ?? "Opciones"}
                </span>
                <div className="mt-3 flex flex-col gap-2.5">
                  {options.map((option, index) => {
                    const isActive = index === selected;
                    return (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() => setSelected(index)}
                        className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-3.5 text-left transition-colors ${
                          isActive
                            ? "border-primary-500 bg-primary-50"
                            : "border-secondary-200 bg-background-50 hover:border-secondary-300"
                        }`}
                      >
                        <span className="flex items-start gap-3">
                          <i
                            className={`mt-0.5 text-lg ${isActive ? "ri-checkbox-circle-fill text-primary-500" : "ri-checkbox-blank-circle-line text-foreground-300"}`}
                          />
                          <span className="flex flex-col">
                            <span className="text-sm font-medium text-foreground-900">{option.label}</span>
                            {option.note ? (
                              <span className="mt-0.5 text-xs text-foreground-500">{option.note}</span>
                            ) : null}
                          </span>
                        </span>
                        <span className="whitespace-nowrap font-heading text-lg font-semibold text-foreground-950">
                          {option.price}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {includes && includes.length > 0 ? (
              <div className="mt-6">
                <span className="font-label text-[11px] uppercase tracking-[0.18em] text-foreground-500">
                  {includesTitle ?? "Incluye"}
                </span>
                <ul className="mt-3 flex flex-col gap-2">
                  {includes.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                      <i className="ri-check-line mt-0.5 text-accent-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {conditions && conditions.length > 0 ? (
              <div className="mt-6">
                <span className="font-label text-[11px] uppercase tracking-[0.18em] text-foreground-500">
                  {conditionsTitle ?? "Condiciones"}
                </span>
                <ul className="mt-3 flex flex-col gap-2">
                  {conditions.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-foreground-600">
                      <i className="ri-information-line mt-0.5 text-foreground-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <a
              href={buildCtaHref(activeOption)}
              target="_blank"
              rel="noreferrer"
              className="cta-primary group mt-8 inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap rounded-[16px] bg-primary-500 px-6 py-4 font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-background-50 transition-all duration-300 hover:bg-primary-600"
            >
              {ctaLabel}
              <i className={`${ctaIcon} text-base transition-transform duration-300 group-hover:translate-x-1`} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}